import {getTestProps} from '@benchling/js-test-utils';
import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import {getActionButtonRoleProps} from 'lib/a11y/ButtonA11y';
import staticURL from 'lib/staticURL';
import {FONT_PROXIMA_14, FONT_PROXIMA_15, FONT_PROXIMA_17} from 'styles/FontConstants';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import {GRAY_DARKER, GRAY_LIGHT, GRAY_LIGHTER, ORANGE_LIGHTEST, WHITE, BLUE, ORANGE} from 'ui-library/Colors';
import {LocalTime} from 'ui-library/Times';

import {type NormalizedRun} from './splitPredictionRuns';

interface PredictionRunsListProps {
  completedRuns?: Array<NormalizedRun>;
  defaultCompletedIconPath?: string;
  disabledMessage?: string;
  loading?: boolean;
  onCreateNewRun: () => void;
  onRunSelected: (run: NormalizedRun) => void;
  pendingRuns?: Array<NormalizedRun>;
  title: string;
}

function MetadataItem({
  label,
  value,
  className,
}: {
  label: string;
  value: string | JSX.Element;
  className?: object;
}): JSX.Element {
  return (
    <div className={css(styles.metadataItem, className)}>
      <span className={css(styles.metadataLabel)}>{label}</span>
      <span className={css(styles.metadataValue)}>{value}</span>
    </div>
  );
}

function MetadataRow({date, displayName}: {date: string; displayName?: string}): JSX.Element {
  return (
    <div className={css(styles.metadataRow)}>
      {displayName != null ? <MetadataItem label="Model" value={displayName} /> : null}
      <MetadataItem label="Date" value={<LocalTime datetime={date} />} className={styles.dateItem} />
    </div>
  );
}

function PendingRunItem({run}: {run: NormalizedRun}): JSX.Element {
  return (
    <div
      className={css(styles.runItem)}
      {...getTestProps({component: 'PredictionRunsList', element: 'pendingRun', key: run.id})}
      data-amp-safe={false}
    >
      <div className={css(styles.runIcon)}>
        <div className={css(styles.iconCircle, styles.inProgressIcon)}>
          <div className={css(styles.spinnerIcon)} />
        </div>
      </div>
      <div className={css(styles.runContent)}>
        <MetadataRow date={run.createdAt} displayName={run.displayName} />
      </div>
    </div>
  );
}

function CompletedRunItem({
  defaultCompletedIconPath,
  onRunSelected,
  run,
}: {
  defaultCompletedIconPath?: string;
  onRunSelected: (run: NormalizedRun) => void;
  run: NormalizedRun;
}): JSX.Element {
  const handleClick = (): void => {
    onRunSelected(run);
  };
  const iconPath = run.iconPath ?? defaultCompletedIconPath;
  return (
    <div
      aria-label={`View ${run.displayName ?? 'model'} run`}
      className={css(styles.runItem, styles.clickableRunItem)}
      {...getActionButtonRoleProps({onPress: handleClick})}
      {...getTestProps({component: 'PredictionRunsList', element: 'completedRun', key: run.id})}
      data-amp-safe={false}
    >
      <div className={css(styles.runIcon)}>
        <div className={css(styles.iconCircle)}>
          {iconPath != null ? (
            <img alt={run.displayName ?? ''} className={css(styles.modelIcon)} src={staticURL(iconPath)} />
          ) : null}
        </div>
      </div>
      <div className={css(styles.runContent)}>
        <MetadataRow date={run.createdAt} displayName={run.displayName} />
      </div>
    </div>
  );
}

export default function PredictionRunsList({
  completedRuns = [],
  defaultCompletedIconPath,
  disabledMessage,
  loading = false,
  onCreateNewRun,
  onRunSelected,
  pendingRuns = [],
  title,
}: PredictionRunsListProps): JSX.Element {
  return (
    <div
      className={css(styles.runsListWrapper)}
      {...getTestProps({component: 'PredictionRunsList', element: 'root'})}
    >
      <div className={css(styles.runsContent)}>
        <div className={css(styles.runsHeader)}>
          <h2 className={css(styles.runsTitle)}>{title}</h2>
          <Button
            buttonStyle={ButtonStyle.PRIMARY}
            icon="sparkle"
            size={ButtonSize.MEDIUM}
            text="Create new run"
            disabled={Boolean(disabledMessage)}
            onClick={onCreateNewRun}
          />
        </div>
        {disabledMessage && <InlineBanner text={disabledMessage} bannerType={InlineBannerType.WARNING} />}

        <h3 className={css(styles.sectionTitle)} id="prediction-runs-pending-heading">
          Pending runs
        </h3>
        {loading ? (
          <div aria-label="Loading..." className={css(styles.loadingRunItem)} role="status">
            <span className={css(styles.loadingText)}>Loading...</span>
          </div>
        ) : pendingRuns?.length && pendingRuns.length > 0 ? (
          pendingRuns.map((run) => <PendingRunItem key={run.id} run={run} />)
        ) : (
          <div className={css(styles.runItem)}>No pending runs.</div>
        )}

        <h3
          className={css(styles.sectionTitle, styles.completedRunsTitle)}
          id="prediction-runs-completed-heading"
        >
          Completed runs
        </h3>
        {loading ? (
          <div aria-label="Loading..." className={css(styles.loadingRunItem)} role="status">
            <span className={css(styles.loadingText)}>Loading...</span>
          </div>
        ) : completedRuns?.length && completedRuns.length > 0 ? (
          completedRuns.map((run) => (
            <CompletedRunItem
              defaultCompletedIconPath={defaultCompletedIconPath}
              key={run.id}
              onRunSelected={onRunSelected}
              run={run}
            />
          ))
        ) : (
          <div className={css(styles.runItem)}>No completed runs.</div>
        )}
      </div>
    </div>
  );
}

const shimmerKeyframes = {
  '0%': {
    transform: 'translateX(-100%)',
  },
  '100%': {
    transform: 'translateX(100%)',
  },
};

const styles = StyleSheet.create({
  runsListWrapper: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
  },
  runsContent: {
    flex: '1 1 auto',
    padding: '20px',
    overflowY: 'auto',
  },
  runsHeader: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '32px',
  },
  runsTitle: {
    ...FONT_PROXIMA_17,
    fontWeight: 'bold',
    margin: '0',
  },
  sectionTitle: {
    ...FONT_PROXIMA_15,
    fontWeight: 'bold',
    margin: '0 0 16px 0',
  },
  runItem: {
    display: 'flex',
    alignItems: 'center',
    padding: '12px 16px',
    borderRadius: '8px',
    border: `1px solid ${GRAY_LIGHT}`,
    marginBottom: '8px',
    backgroundColor: WHITE,
  },
  clickableRunItem: {
    cursor: 'pointer',
    transition: 'background-color 0.2s ease, border-color 0.2s ease',
    ':hover': {
      backgroundColor: GRAY_LIGHTER,
      borderColor: GRAY_LIGHT,
    },
    ':focus': {
      outline: `2px solid ${BLUE}`,
      outlineOffset: '2px',
    },
  },
  runIcon: {
    marginRight: '12px',
  },
  iconCircle: {
    width: '40px',
    height: '40px',
    borderRadius: '50%',
    backgroundColor: GRAY_LIGHTER,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  inProgressIcon: {
    backgroundColor: ORANGE_LIGHTEST,
  },
  runContent: {
    flex: '1 1 auto',
  },
  metadataRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '30px',
    marginLeft: '16px',
  },
  metadataItem: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: '2px',
  },
  metadataLabel: {
    ...FONT_PROXIMA_14,
    color: GRAY_DARKER,
    fontWeight: 'bold',
  },
  metadataValue: {
    ...FONT_PROXIMA_14,
    color: GRAY_DARKER,
  },
  spinnerIcon: {
    width: '20px',
    height: '20px',
    border: `2px solid ${ORANGE}`,
    borderTop: '2px solid transparent',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
  },
  modelIcon: {
    width: '20px',
    height: '20px',
    objectFit: 'contain',
  },
  dateItem: {
    minWidth: '130px',
  },
  completedRunsTitle: {
    marginTop: '32px',
  },
  loadingRunItem: {
    display: 'flex',
    alignItems: 'center',
    padding: '12px 16px',
    borderRadius: '8px',
    marginBottom: '8px',
    border: `1px solid ${GRAY_LIGHT}`,
    backgroundColor: WHITE,
    position: 'relative',
    overflow: 'hidden',
    ':before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      background: `linear-gradient(90deg, transparent 0%, ${GRAY_LIGHTER} 50%, transparent 100%)`,
      animationName: [shimmerKeyframes],
      animationDuration: '1.8s',
      animationIterationCount: 'infinite',
      animationTimingFunction: 'ease-in-out',
      pointerEvents: 'none',
      zIndex: 0,
    },
  },
  loadingText: {
    position: 'relative',
    zIndex: 1,
  },
});
